/**
 * Form field components bound to react-hook-form context. Every field shows its
 * label, optional hint and validation message consistently.
 */
import type { Option } from "@shared/constants.ts";
import { penceToDecimal } from "@shared/money.ts";
import type { ReactNode } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Controller, type FieldValues, type UseFormReturn, useFormContext } from "react-hook-form";
import { getIn } from "../../lib/forms.ts";

interface BaseProps {
  name: string;
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  className?: string;
}

function useFieldError(name: string): string | undefined {
  const { formState } = useFormContext();
  return (getIn(formState.errors, name) as { message?: string } | undefined)?.message;
}

function Wrapper({ name, label, hint, required, className, children }: BaseProps & { children: ReactNode }) {
  const error = useFieldError(name);
  return (
    <Form.Group className={className ?? "mb-3"} controlId={`f-${name}`}>
      <Form.Label>
        {label}
        {required && (
          <span className="text-danger ms-1" aria-hidden="true">
            *
          </span>
        )}
      </Form.Label>
      {children}
      {error ? (
        <Form.Control.Feedback type="invalid" className="d-block" role="alert">
          {error}
        </Form.Control.Feedback>
      ) : (
        hint && <Form.Text className="text-muted d-block">{hint}</Form.Text>
      )}
    </Form.Group>
  );
}

export function TextField(
  props: BaseProps & { type?: string; placeholder?: string; autoComplete?: string; autoFocus?: boolean },
) {
  const { register } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Form.Control
        type={props.type ?? "text"}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
        autoFocus={props.autoFocus}
        isInvalid={!!error}
        aria-required={props.required}
        {...register(props.name)}
      />
    </Wrapper>
  );
}

export function TextAreaField(props: BaseProps & { rows?: number; placeholder?: string }) {
  const { register } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Form.Control
        as="textarea"
        rows={props.rows ?? 3}
        placeholder={props.placeholder}
        isInvalid={!!error}
        {...register(props.name)}
      />
    </Wrapper>
  );
}

/** Controlled so a value set before its options load (e.g. from ?propertyId=) is kept. */
export function SelectField(
  props: BaseProps & {
    options: ReadonlyArray<Option>;
    placeholder?: string;
    onChangeValue?: (value: string) => void;
  },
) {
  const { control } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <Form.Select
            isInvalid={!!error}
            aria-required={props.required}
            name={field.name}
            ref={field.ref}
            onBlur={field.onBlur}
            value={field.value ?? ""}
            onChange={(e) => {
              field.onChange(e.target.value);
              props.onChangeValue?.(e.target.value);
            }}
          >
            {props.placeholder !== undefined && <option value="">{props.placeholder}</option>}
            {props.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        )}
      />
    </Wrapper>
  );
}

/** Pounds input. Accepts numbers (pence) as initial values and edits as a pounds string. */
export function MoneyField(props: BaseProps & { placeholder?: string }) {
  const { control } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <InputGroup hasValidation>
            <InputGroup.Text>£</InputGroup.Text>
            <Form.Control
              inputMode="decimal"
              placeholder={props.placeholder ?? "0.00"}
              isInvalid={!!error}
              aria-required={props.required}
              name={field.name}
              ref={field.ref}
              onBlur={field.onBlur}
              value={typeof field.value === "number" ? penceToDecimal(field.value) : (field.value ?? "")}
              onChange={(e) => field.onChange(e.target.value)}
            />
          </InputGroup>
        )}
      />
    </Wrapper>
  );
}

export function DateField(props: BaseProps) {
  const { register } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Form.Control
        type="date"
        isInvalid={!!error}
        aria-required={props.required}
        {...register(props.name)}
      />
    </Wrapper>
  );
}

function toLocalInput(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  if (!value.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(value)) return value.slice(0, 16);
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local date-time input; the schema converts it to an ISO timestamp. */
export function DateTimeField(props: BaseProps) {
  const { control } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Controller
        control={control}
        name={props.name}
        render={({ field }) => (
          <Form.Control
            type="datetime-local"
            isInvalid={!!error}
            name={field.name}
            ref={field.ref}
            onBlur={field.onBlur}
            value={toLocalInput(field.value)}
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
    </Wrapper>
  );
}

export function CheckboxField({ name, label, hint }: { name: string; label: ReactNode; hint?: ReactNode }) {
  const { register } = useFormContext();
  return (
    <Form.Group className="mb-3" controlId={`f-${name}`}>
      <Form.Check type="switch" label={label} {...register(name)} />
      {hint && <Form.Text className="text-muted">{hint}</Form.Text>}
    </Form.Group>
  );
}

export function NumberField(props: BaseProps & { min?: number; max?: number; step?: number }) {
  const { register } = useFormContext();
  const error = useFieldError(props.name);
  return (
    <Wrapper {...props}>
      <Form.Control
        type="number"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        isInvalid={!!error}
        {...register(props.name)}
      />
    </Wrapper>
  );
}

/** Form-level error summary shown above the actions. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger py-2" role="alert">
      {message}
    </div>
  );
}

export type AnyForm = UseFormReturn<FieldValues>;
